import { Button, type ColumnDef, DataTable } from "@monbits/ui";
import { useQuery } from "@tanstack/react-query";
import { Check, CheckCircle, Copy, ShieldOff } from "lucide-react";
import * as React from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { StatusBadge } from "src/components/status-badge";
import { BLOCKCHAIN_NAMES } from "src/constants/blockchains";
import walletProvider, {
	type OutboundWalletsResponse,
	type WalletDetails,
} from "src/providers/wallet-provider";

export function OutboundWalletsList() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [copiedAddress, setCopiedAddress] = React.useState<string | null>(null);

	const { data, isLoading, refetch } = useQuery({
		queryKey: ["outbound-wallets"],
		queryFn: async () => {
			const response: OutboundWalletsResponse =
				await walletProvider.listOutboundWallets();
			return response.wallets;
		},
	});

	const blockchainFilter = searchParams.get("blockchain") || "";
	const wallets = React.useMemo(() => {
		const all = data ?? [];
		return blockchainFilter
			? all.filter((w) => w.blockchain === blockchainFilter)
			: all;
	}, [data, blockchainFilter]);

	const handleViewWallet = React.useCallback(
		(wallet: WalletDetails) => {
			const queryString = searchParams.toString();
			const targetPath = queryString
				? `/wallets/outbound/${wallet.id}?${queryString}`
				: `/wallets/outbound/${wallet.id}`;
			navigate(targetPath);
		},
		[navigate, searchParams],
	);

	const handleCopyAddress = React.useCallback(
		(address: string, e: React.MouseEvent) => {
			e.stopPropagation();
			navigator.clipboard.writeText(address);
			setCopiedAddress(address);
			toast.success("Address copied to clipboard");
			setTimeout(() => setCopiedAddress(null), 2000);
		},
		[],
	);

	// Expose refetch for child components (drawer)
	React.useEffect(() => {
		(
			window as unknown as { refetchOutboundWallets?: () => void }
		).refetchOutboundWallets = refetch;
		return () => {
			delete (window as unknown as { refetchOutboundWallets?: () => void })
				.refetchOutboundWallets;
		};
	}, [refetch]);

	const columns: ColumnDef<WalletDetails>[] = React.useMemo(
		() => [
			{
				id: "network",
				header: "Network",
				cell: (row) => (
					<div className="flex items-center gap-2">
						<CryptoIcon ticker={row.blockchain} className="h-5 w-5" />
						<span className="whitespace-nowrap">
							{BLOCKCHAIN_NAMES[row.blockchain] || row.blockchain}
						</span>
					</div>
				),
			},
			{
				id: "address",
				header: "Address",
				cell: (row) => (
					<div className="flex items-center gap-2">
						<span className="font-mono text-sm">{row.address}</span>
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6"
							onClick={(e) => handleCopyAddress(row.address, e)}
						>
							{copiedAddress === row.address ? (
								<Check className="h-3 w-3 text-green-600" />
							) : (
								<Copy className="h-3 w-3" />
							)}
						</Button>
					</div>
				),
			},
			{
				id: "status",
				header: "Status",
				cell: (row) =>
					row.blockedAt ? (
						<StatusBadge type="error" icon={ShieldOff}>
							Blocked
						</StatusBadge>
					) : (
						<StatusBadge type="success" icon={CheckCircle}>
							Active
						</StatusBadge>
					),
			},
			{
				id: "transactions",
				header: "Transactions",
				cell: (row) => (
					<div className="text-sm">
						<div>Mainnet: {row.transactionCounts?.mainnet?.confirmed || 0}</div>
						{row.transactionCounts?.testnet &&
							row.transactionCounts.testnet.confirmed > 0 && (
								<div className="text-muted-foreground">
									Testnet: {row.transactionCounts.testnet.confirmed}
								</div>
							)}
					</div>
				),
			},
		],
		[copiedAddress, handleCopyAddress],
	);

	return (
		<>
			{blockchainFilter && (
				<div className="mb-4 flex items-center justify-between rounded-lg border bg-muted/40 px-4 py-2 text-sm">
					<span className="text-muted-foreground">
						Showing wallets for{" "}
						{BLOCKCHAIN_NAMES[blockchainFilter] || blockchainFilter}.
					</span>
					<button
						type="button"
						className="text-primary hover:underline"
						onClick={() => {
							const next = new URLSearchParams(searchParams);
							next.delete("blockchain");
							setSearchParams(next);
						}}
					>
						Clear filter
					</button>
				</div>
			)}

			<DataTable
				columns={columns}
				data={wallets}
				isLoading={isLoading}
				onRowClick={handleViewWallet}
				getRowId={(row) => String(row.id)}
				emptyMessage="No outbound wallets"
				emptyDescription={
					blockchainFilter
						? "No outbound wallet for this network"
						: "Outbound wallets will appear here"
				}
			/>

			<Outlet />
		</>
	);
}
